import { ReelContainer } from '@/containers/reels/ReelContainer'
import { ReelsContainerHelper } from '@/containers/reels/ReelsContainerHelper'
import { ReelsStripConfig } from '@/containers/reels/ReelsStripConfig'
import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolContainer } from '@/containers/symbols/SymbolContainer'
import { SymbolLayerType } from '@/containers/symbols/SymbolLayerType'
import { SymbolType } from '@/containers/symbols/SymbolType'
import Facade from '@/Facade'
import {
    CascadeSpinData,
    FreeSpinsActivateData,
    IInitReelsData,
    IReelsTimingsConfig,
    ISpinResponse,
    IStoppingReelsData,
    ShowRegularWinningsData,
    SymbolData,
} from '@/modules'
import { MobileSwipeSpinController, UIAPI, UIEvent } from '@dragonframework/modules'
import GameSignals from '@/signals/GameSignals'
import * as PIXI from 'pixi.js'
import gsap from 'gsap'
import { ContainersNames } from '@/containers/ContainersNames'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import {
    getJackpotLadderCollectTarget,
    getLadderRemainingCoinCapacity,
} from '@/gameConfigs/components/JackpotLaddersComponent'
import { isFeatureRoundActive, isSwipeBlockedByGameState } from '@/utils/SwipeGating'

export class ReelsContainer extends PIXI.Container {
    private static readonly COIN_ANTICIPATION_DIM_POWER = 0.55
    private static readonly FREE_SPINS_SCATTER_FOCUS_DIM_POWER = 0.55
    // Match win-line SHADOW_OPACITY (0.55); previous 0.82 left non-coins almost black.
    private static readonly FS_COLLECT_COIN_FOCUS_DIM_POWER = 0.55
    private static readonly FS_COLLECTED_COIN_DIM_POWER = 0.55
    private static readonly ANTICIPATION_DELAY_SCALE = 0.58
    private static readonly SCATTER_ANTICIPATION_PER_REEL_MS = 500
    private static readonly FEATURE_ANTICIPATION_PER_REEL_MS = 600
    private static readonly BASE_GAME_MASK_Y = 6.5
    private static readonly FREE_SPINS_MASK_Y = 6.5
    private static readonly REELS_MASK_X = 1.5
    private static readonly REELS_MASK_DRAW_Y = 0
    private static readonly REELS_MASK_WIDTH = 1151.5
    private static readonly REELS_MASK_HEIGHT = 663.5
    private static readonly SPIN_MASK_DRAW_Y = 0
    private static readonly SPIN_MASK_HEIGHT = 663.5
    private static readonly DEBUG_SHOW_REELS_MASK = false
    private static readonly DEBUG_PLAY_FIRST_CELL_WILD_ON_LOAD = true
    private static readonly WIN_LOOP_SHOW_MS = 2200
    private static readonly WIN_LOOP_RESET_MS = 180
    private static readonly HOLD_AND_WIN_TRIGGER_ANIMATION_FALLBACK_MS = 1800
    private static readonly FS_COIN_COLLECT_GLOW_NAME = 'coin_step3'
    private static readonly FS_COIN_COLLECT_GLOW_SCALE = 0.85
    private static readonly FS_COIN_COLLECT_GLOW_ON_COIN_SCALE_FACTOR = 2
    private static readonly FS_COIN_COLLECT_HOLD_SECONDS = 0.5
    private static readonly FS_COIN_COLLECT_FLY_SECONDS = 0.58
    private static readonly FS_COIN_COLLECT_SKIP_TIME_SCALE = 3
    private static readonly FS_COIN_COLLECT_TARGET_SCALE = 0.25

    private _pendingWinningAnimations: number = 0
    protected _symbolLayers: PIXI.Container[]
    protected _reels: ReelContainer[]
    protected _reelsContainerTopUnmasked: PIXI.Container
    protected _reelsContainer: PIXI.Container
    protected _reelsContainerBotUnmasked: PIXI.Container
    protected _anticipationIndices: number[]
    protected _activeReelsStrip: number[][]
    protected _scattersCount: number
    protected _isSpinning: boolean
    protected _isQuickStop: boolean
    protected _immediatelyStopped: boolean
    protected _speedSettings: IReelsTimingsConfig
    protected _cropMask: PIXI.Graphics
    protected _cropMaskDebug: PIXI.Graphics | null = null
    protected _cascadeIndex: number
    protected _cascadeSpinsCount: number
    protected _isFirstWildAnimation: boolean
    private _reelsStopTimers: Array<ReturnType<typeof setTimeout>> = []
    private _mobileSwipeSpinController: MobileSwipeSpinController | null = null
    private _isMobileSwipeSpinFeatureEnabled: boolean = false
    private _hasShownMobileSwipeSpinPopup: boolean = false
    private _winLoopCombinations: number[][][] = []
    private _currentWinLoopIndex: number = 0
    private _winLoopTimer: ReturnType<typeof setTimeout> | null = null
    private _winLoopResetTimer: ReturnType<typeof setTimeout> | null = null
    private _isWinPresentationActive: boolean = false
    private _isLineByLineDrivingWins: boolean = false
    private _isCoinRespinAnticipationActive: boolean = false
    private _isCoinRespinAnticipationVisible: boolean = false
    private _isScatterAnticipationActive: boolean = false
    private _isScatterAnticipationVisible: boolean = false
    private _isHoldAndWinTriggerCoinWinActive: boolean = false
    private _isFreeSpinsTriggerScatterFocusActive: boolean = false
    private _freeSpinsTriggerScatterPositions: number[][] = []
    private _freeSpinsTriggerScatterFocusRestoreTween: gsap.core.Tween | null = null
    private _isFsCollectCoinFocusActive: boolean = false
    private _isFsCollectCoinFocusApplied: boolean = false
    private _fsCoinCollectGlowSpines: SpineAnimation[] = []
    private _fsCoinCollectGlowTweens: gsap.core.Animation[] = []
    private _pendingFsCollectCount: number = 0
    private _fsCollectExpectedHits: number = 0
    private _fsCollectProcessedHits: number = 0
    private _pendingFsCollectMultipliers: number[] = []
    private _isFsCollectFastForward: boolean = false
    private _fsCollectFinalLadderCount: number = -1
    private _fsCollectFinalMilestones: number[] = []
    private _pendingAnticipationReels: Set<number> = new Set<number>()
    private _anticipationPerReelDelayMs: number = 0
    /** Cascade applied to chained reels that are not themselves anticipating — 0 under turbo. */
    private _chainedReelStopDelayMs: number = 0
    private _hasPlayedFirstCellWildDebug: boolean = false
    private _baseGameGridSnapshot: { reels: number[][]; originalGrid: SymbolData[][] } | null = null
    private _freeSpinsPopupIdleLocks: Set<string> = new Set<string>()
    private _freeSpinsPopupPausedSymbols: Set<SymbolContainer> = new Set<SymbolContainer>()

    public constructor() {
        super()
        GameSignals.createReels.connect(this.onCreateReels)
        GameSignals.startSpin.connect(this.onStartSpin)
        GameSignals.reelJustTouched.connect(this.onReelJustTouched)
        GameSignals.reelStopped.connect(this.onReelStopped)
        GameSignals.stoppingReels.connect(this.onSpinStoping)
        GameSignals.immediatelyStop.connect(this.onImmediateStop)
        GameSignals.errorStop.connect(this.onErrorStop)
        GameSignals.showFreeSpinsIntro.connect(this.onFreeSpinsIntro)
        GameSignals.showFreeSpinsSummaryComplete.connect(this.onFreeSpinsComplete)
        // The base transition wave covers the reels here, so the grid swap stays hidden.
        GameSignals.showBaseGameBackground.connect(this.restoreBaseGameGridSnapshot)
        GameSignals.activateScatters.connect(this.onActivateScatters)
        GameSignals.showRegularWin.connect(this.onShowRegularWin)
        GameSignals.cascadeSpin.connect(this.onCascadeSpin)
        GameSignals.multiplierState.connect(this.onMultiplierState)
        GameSignals.finalState.connect(this.onFinalState)
        GameSignals.idle.connect(this.onIdleState)
        GameSignals.stopButtonClicked.connect(this.onSkipWinLoop)
        GameSignals.spinButtonClicked.connect(this.onSkipWinLoop)
        GameSignals.changeBet.connect(this.onBetChanged)
        ;(GameSignals as any).settingsMenuOpened?.connect(this.onBetChanged)
        ;(GameSignals as any).openBuyBonusPopup?.connect(this.onBetChanged)
        GameSignals.changeDialogState.connect(this.onChangeDialogState)
        GameSignals.showSuspenseAnimation.connect(this.onShowSuspenseAnimation)
        GameSignals.hideSuspenseAnimation.connect(this.onHideSuspenseAnimation)
        ;(GameSignals as any).featureIntroRequested?.connect(this.onFeatureTransitionStart)
        ;(GameSignals as any).holdAndWinTransitionStart?.connect(this.onFeatureTransitionStart)
        ;(GameSignals as any).featureIntroContinue?.connect(this.onFeatureIntroContinue)
        ;(GameSignals as any).showWinningsComplete?.connect(this.onShowWinningsComplete)
        ;(GameSignals as any).lineByLineStepChanged?.connect(this.onLineByLineStepChanged)
        ;(GameSignals as any).lineByLinePause?.connect(this.onLineByLinePause)
        ;(GameSignals as any).lineByLineLoopStopped?.connect(this.onLineByLineLoopStopped)
        ;(GameSignals as any).fsCollectSequenceRequested?.connect(this.onFsCollectSequenceRequested)
        ;(GameSignals as any).fsCollectSequenceSkip?.connect(this.onFsCollectSequenceSkip)
        ;(GameSignals as any).fsCollectSequenceForceComplete?.connect(this.finishFsCollectSequenceNow)
        ;(GameSignals as any).holdAndWinStop?.connect(this.onHoldAndWinStop)
        ;(GameSignals as any).holdAndWinTriggerCoinWinStart?.connect(this.onHoldAndWinTriggerCoinWinStart)
        ;(GameSignals as any).jackpotPopupStart?.connect(() => this.onFreeSpinsPopupStart('jackpot'))
        ;(GameSignals as any).jackpotPopupComplete?.connect(() => this.onFreeSpinsPopupComplete('jackpot'))
        // bigWinPresentation fires before the overlay is visible — freeze payline wins ASAP.
        GameSignals.bigWinPresentation.connect(() => this.onFreeSpinsPopupStart('commendation'))
        GameSignals.showBigWinComplete.connect(() => this.onFreeSpinsPopupComplete('commendation'))
        ;(GameSignals as any).jackpotLadderUpgradeStart?.connect(() => this.onFreeSpinsPopupStart('upgrade'))
        ;(GameSignals as any).jackpotLadderUpgradeComplete?.connect(() => this.onFreeSpinsPopupComplete('upgrade'))
        GameSignals.enableSwipeFeature.connect(this.onEnableSwipeFeature)
        GameSignals.swipeFeaturePopupShown.connect(this.onSwipeFeaturePopupShown)
        // Swipe must stay inactive on preloader / intro until the player enters base game.
        GameSignals.completeSplashScreenState.connect(this.onCompleteSplashScreenStateForSwipe)
        GameSignals.spinResponse.connect((data: ISpinResponse) => {
            this._cascadeSpinsCount = data.winning_combinations.length - 1
        })

        this.changeSpinSettings(Facade.isTurboSpin)
        this._isFirstWildAnimation = true
    }

    protected onFinalState = () => {
        if (this._isHoldAndWinTriggerCoinWinActive) {
            return
        }

        this.visible = !this.isBaseRevealBlocked()
        if (this.visible) {
            this.restoreBaseSymbolsVisualState()
        }

        const hadWinPresentation =
            this._isWinPresentationActive || this._winLoopCombinations.length > 0 || this._pendingWinningAnimations > 0

        this.stopWinLoop()
        if (hadWinPresentation) {
            this.stopHighPayIdle(true)
        }

        this._reels.forEach((reel: ReelContainer) => {
            gsap.killTweensOf(reel)
            reel.stopScattersAnimation()
        })
    }

    protected onIdleState = () => {
        if (this._isHoldAndWinTriggerCoinWinActive) {
            return
        }

        // After total-win → line-by-line, the state machine often enters IDLE while the
        // payline loop is still running. restoreBaseSymbolsVisualState() shadowOuts every
        // symbol and wiped the first line's dimming (later lines looked fine because idle
        // only emits once).
        const winPresentationActive = this.hasActiveWinPresentation()

        if (!this.isBaseRevealBlocked()) {
            this.visible = true
            if (!winPresentationActive) {
                this.restoreBaseSymbolsVisualState()
            }
        }

        if (this._isSpinning || winPresentationActive) {
            return
        }

        this.playHighPayIdle()
    }

    protected onCascadeSpin = (data: CascadeSpinData) => {
        this._cascadeIndex++
        let suspenseReelIndex = 0
        this.symbolsShadowOut()
        let delay: number = 0
        const reelsWinPositions: number[] = []
        let cascadeSpinIndex: number = 0
        let suspenseDelay: number = 0
        const suspenseReelsIndexes: number[] = []

        if (!data.cascadeReels.length) {
            GameSignals.completeCascadeSpin.emit()
            return
        }

        data.cascadeReels.forEach((reelIndex: number, index: number) => {
            const reel: ReelContainer = this._reels[reelIndex]
            const cascadeReelsCount: number = data.cascadeReels.length

            delay += this._speedSettings.DELAY_BETWEEN_CASCADE_REELS

            const reelWinPositions: number[][] = data.winPositions.filter((position: number[]) => {
                return position[0] === reelIndex
            })

            reelsWinPositions.push(reelWinPositions.length)
            reel.updateHiddenSymbols(data.slotGrid[reelIndex], reelWinPositions)

            if (data.suspenseSpin) {
                // suspenseDelay += Facade.anticipationConfig.ANTICIPATION_REEL_TIME
                // gsap.to(reel, {
                //     duration: suspenseDelay,
                //     onComplete: () => {
                //         const duration: number = reel.suspenseSpinStage1() + Facade.anticipationConfig.DELAY_BETWEEN_STAGE1_AND_STAGE2 // this parameter can be like positive as negative
                //
                //         gsap.to(this, {
                //             duration,
                //             onComplete: () => {
                //                 suspenseReelIndex++
                //                 reel.suspenseSpinStage2(suspenseReelIndex === cascadeReelsCount)
                //             }
                //         })
                //     }
                // })
                // return
                suspenseReelsIndexes.push(reelIndex)
                gsap.to(reel, {
                    duration: delay,
                    onComplete: () => {
                        const delayBeforeSuspense: number =
                            reel.suspenseSpinStage1() + Facade.anticipationConfig.DELAY_BETWEEN_STAGE1_AND_STAGE2

                        if (index === data.cascadeReels.length - 1) {
                            suspenseReelsIndexes.forEach((reelIndex: number) => {
                                const reel: ReelContainer = this._reels[reelIndex]

                                gsap.to(reel, {
                                    duration: delayBeforeSuspense,
                                    onComplete: () => {
                                        if (!suspenseReelIndex) {
                                            GameSignals.symbolsDrop.emit()
                                        }
                                        suspenseReelIndex++
                                        reel.suspenseSpinStage2(suspenseReelIndex === cascadeReelsCount)
                                    },
                                })
                            })
                        }
                    },
                })
                return
            }

            gsap.to(reel, {
                duration: delay,
                onComplete: () => {
                    if (!cascadeSpinIndex) {
                        GameSignals.symbolsDrop.emit()
                    }
                    cascadeSpinIndex++
                    reel.cascadeSpin(cascadeSpinIndex === cascadeReelsCount)
                },
            })
        })
    }

    protected onShowRegularWin = (data: ShowRegularWinningsData) => {
        this.stopHighPayIdle()
        this.stopWinLoop(false)

        if (this.shouldSkipWinningSymbolAnimationsForFreeSpinsIntro()) {
            this._isWinPresentationActive = false
            this._winLoopCombinations = []
            this._currentWinLoopIndex = 0
            this._pendingWinningAnimations = 0
            this._isLineByLineDrivingWins = false
            GameSignals.regularWinOutAnimationsComplete.emit()
            return
        }

        this._isWinPresentationActive = true
        this._winLoopCombinations = data.combinations.map((combination) => combination.symbols_positions)
        this._currentWinLoopIndex = 0
        this._pendingWinningAnimations = 0
        this._isLineByLineDrivingWins = false

        // Total-win phase: animate all winning symbols together (required).
        this.presentWinningPositions(data.winPositions, false, 'showRegularWin')

        if (this._pendingWinningAnimations === 0) {
            GameSignals.regularWinOutAnimationsComplete.emit()
        }
    }

    private shouldSkipWinningSymbolAnimationsForFreeSpinsIntro(): boolean {
        const freeSpinsAwarded = Number(Facade.model?.spinData?.bonus?.freebet_given || 0)

        return freeSpinsAwarded > 0 && !Facade.model?.roundState?.isFreeSpins
    }

    private isAutoplayActive(): boolean {
        return !!Facade.isAuto
    }

    private presentWinningPositions(winningPositions: number[][], loop: boolean, source: string = 'unknown'): void {
        if (loop) {
            this.presentLineByLineStepWins(winningPositions)
            return
        }

        const winningSet = new Set(
            (winningPositions || [])
                .filter((position) => Array.isArray(position) && position.length >= 2)
                .map((position) => `${Number(position[0])}:${Number(position[1])}`)
        )
        const extraTop = Number(Facade.reelsConfig.EXTRA_SYMBOLS_TOP || 0)
        const visibleRows = Number(Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL || 0)
        const actions: Array<{ reel: number; row: number; index: number; action: string }> = []

        this._reels.forEach((reel: ReelContainer, reelIndex: number) => {
            reel.symbols.forEach((symbol: SymbolContainer, symbolIndex: number) => {
                const visibleRowIndex = symbolIndex - extraTop
                const isVisibleRow = visibleRowIndex >= 0 && visibleRowIndex < visibleRows
                const isWinningSymbol = isVisibleRow && winningSet.has(`${reelIndex}:${visibleRowIndex}`)
                const canPlayWinAnimation = this.canPlayWinAnimation(symbol)

                symbol.stopHpOutOverlayAnimation()

                if (isWinningSymbol) {
                    return
                }

                if (this.isHighPaySymbol(symbol.index)) {
                    if (symbol.isPlayingLoopingAnimation('win') || symbol.animation) {
                        this.playHighPayIdleSymbol(symbol)
                    }
                } else if (canPlayWinAnimation || symbol.animation) {
                    symbol.forceStatic()
                }

                symbol.shadowIn(false)
            })
        })

        this._reels.forEach((reel: ReelContainer, reelIndex: number) => {
            reel.symbols.forEach((symbol: SymbolContainer, symbolIndex: number) => {
                const visibleRowIndex = symbolIndex - extraTop
                const isVisibleRow = visibleRowIndex >= 0 && visibleRowIndex < visibleRows
                const isWinningSymbol = isVisibleRow && winningSet.has(`${reelIndex}:${visibleRowIndex}`)
                const canPlayWinAnimation = this.canPlayWinAnimation(symbol)

                if (!isWinningSymbol) {
                    return
                }

                symbol.stopHpOutOverlayAnimation()
                symbol.shadowOut(false)
                symbol.visible = true

                if (canPlayWinAnimation) {
                    this._pendingWinningAnimations++
                    symbol.playAnimation('win', false, this.onWinningAnimationComplete)
                    actions.push({
                        reel: reelIndex,
                        row: visibleRowIndex,
                        index: symbol.index,
                        action: 'play-win-once',
                    })
                } else {
                    symbol.forceStatic()
                    actions.push({
                        reel: reelIndex,
                        row: visibleRowIndex,
                        index: symbol.index,
                        action: 'static-winner',
                    })
                }
            })
        })
    }

    /** One payline: stop everyone else, start this line's wins together from t=0. */
    private presentLineByLineStepWins(winningPositions: number[][]): void {
        const winningSet = new Set(
            (winningPositions || [])
                .filter((position) => Array.isArray(position) && position.length >= 2)
                .map((position) => `${Number(position[0])}:${Number(position[1])}`)
        )
        const extraTop = Number(Facade.reelsConfig.EXTRA_SYMBOLS_TOP || 0)
        const visibleRows = Number(Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL || 0)
        const winners: SymbolContainer[] = []
        const leavers: SymbolContainer[] = []

        // Pass 1: leave total-win cleanly — freeze leavers and dim EVERY visible symbol first.
        // Needed on the first total-win → line-by-line handoff: former total-win winners that are
        // not on this payline can otherwise stay bright (spine still attached / tint not re-applied).
        this._reels.forEach((reel: ReelContainer, reelIndex: number) => {
            reel.symbols.forEach((symbol: SymbolContainer, symbolIndex: number) => {
                const visibleRowIndex = symbolIndex - extraTop
                const isVisibleRow = visibleRowIndex >= 0 && visibleRowIndex < visibleRows
                const isWinningSymbol = isVisibleRow && winningSet.has(`${reelIndex}:${visibleRowIndex}`)
                const canPlayWinAnimation = this.canPlayWinAnimation(symbol)

                symbol.stopHpOutOverlayAnimation()
                symbol.visible = true

                if (!isWinningSymbol) {
                    if (canPlayWinAnimation || symbol.isPlayingAnimation('win') || symbol.animation) {
                        symbol.freezeAsLineByLineLeaver()
                    }
                    leavers.push(symbol)
                    symbol.shadowIn(false)
                    return
                }

                if (canPlayWinAnimation) {
                    winners.push(symbol)
                } else {
                    symbol.forceStatic()
                }
            })
        })

        // Pass 2: light winners and sync their win loops.
        for (const symbol of winners) {
            symbol.shadowOut(false)
            symbol.ensureAnimationAttached()
        }
        for (const symbol of winners) {
            symbol.restartWinLoopSynced()
        }
        for (const symbol of winners) {
            symbol.syncWinTrackToStart()
        }

        // Pass 3: re-assert dim after winner spine attach/restart (can refresh pooled tint/light).
        for (const symbol of leavers) {
            symbol.shadowIn(false)
        }
    }

    /** Pause between paylines: no win clips playing. */
    private clearLineByLineWins(): void {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                symbol.stopHpOutOverlayAnimation()

                if (this.canPlayWinAnimation(symbol) || symbol.isPlayingAnimation('win') || symbol.animation) {
                    symbol.freezeAsLineByLineLeaver()
                }

                symbol.visible = true
                symbol.shadowOut(false)
            })
        })
    }

    private canPlayWinAnimation(symbol: SymbolContainer): boolean {
        if (!symbol) {
            return false
        }

        return this.isHighPaySymbol(symbol.index) || this.isLowPayWinSymbol(symbol.index) || this.isWildSymbol(symbol)
    }

    private isHighPaySymbol(symbolIndex: number): boolean {
        return (
            symbolIndex === SymbolType.HP_1 ||
            symbolIndex === SymbolType.HP_2 ||
            symbolIndex === SymbolType.HP_3 ||
            symbolIndex === SymbolType.HP_4
        )
    }

    private isLowPayWinSymbol(symbolIndex: number): boolean {
        return (
            symbolIndex === SymbolType.LP_1 ||
            symbolIndex === SymbolType.LP_2 ||
            symbolIndex === SymbolType.LP_3 ||
            symbolIndex === SymbolType.LP_4
        )
    }

    private isWildSymbol(symbol: SymbolContainer): boolean {
        if (!symbol) {
            return false
        }

        return (
            symbol.index === SymbolType.LP_5 ||
            String((symbol as any)?._staticTextureName || '')
                .toLowerCase()
                .includes('wild')
        )
    }

    private onWinningAnimationComplete = (): void => {
        if (this._pendingWinningAnimations <= 0) {
            return
        }

        this._pendingWinningAnimations--

        if (this._pendingWinningAnimations === 0) {
            GameSignals.regularWinOutAnimationsComplete.emit()
        }
    }

    private onShowWinningsComplete = () => {
        if (this._isHoldAndWinTriggerCoinWinActive) {
            return
        }

        this._isWinPresentationActive = false

        if (this.isAutoplayActive()) {
            this.stopWinLoop()
            return
        }

        // During free spins we keep total-win display without entering win-loop.
        if (Facade.model?.roundState?.isFreeSpins) {
            this.stopWinLoop()
            return
        }

        if (this.shouldSkipWinningSymbolAnimationsForFreeSpinsIntro()) {
            this.stopWinLoop()
            return
        }

        if (!this._winLoopCombinations.length || this._isSpinning) {
            return
        }

        // Mark before any IDLE handoff so onIdleState won't undim the first payline.
        this._isLineByLineDrivingWins = true

        this.clearWinLoopTimer()
        this.clearWinLoopResetTimer()
    }

    private onLineByLineStepChanged = (data: { combination?: { symbols_positions?: number[][] } }) => {
        if (this._isHoldAndWinTriggerCoinWinActive || this._isSpinning || this._freeSpinsPopupIdleLocks.size > 0) {
            return
        }

        const positions = data?.combination?.symbols_positions
        if (!Array.isArray(positions) || !positions.length) {
            return
        }

        this._isLineByLineDrivingWins = true
        this.clearWinLoopTimer()
        this.clearWinLoopResetTimer()
        this._isWinPresentationActive = false

        this.presentLineByLineStepWins(positions)
    }

    private onLineByLinePause = () => {
        if (this._isHoldAndWinTriggerCoinWinActive || this._isSpinning) {
            return
        }

        this.clearLineByLineWins()
    }

    private onLineByLineLoopStopped = () => {
        if (this._isHoldAndWinTriggerCoinWinActive) {
            return
        }

        this._isLineByLineDrivingWins = false
        this.stopWinLoop()
        this.onIdleState()
    }

    private hasActiveWinPresentation = (): boolean => {
        return (
            this._isWinPresentationActive ||
            this._isLineByLineDrivingWins ||
            this._winLoopCombinations.length > 0 ||
            this._pendingWinningAnimations > 0
        )
    }

    /** Hard-detach looping win spines so they cannot linger into the next spin. */
    private forceClearWinAnimations = () => {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                symbol.stopHpOutOverlayAnimation()
                if (symbol.animation) {
                    symbol.forceStatic()
                }
                symbol.visible = true
                symbol.shadowOut(false)
            })
        })
    }

    private onSkipWinLoop = () => {
        if (this._isSpinning) {
            return
        }

        // Collect skip is owned by FsCoinCollectState. Clearing win spines here
        // can kill in-flight coin tweens and leave the collect state waiting.
        if (this._pendingFsCollectCount > 0 || this._isFsCollectCoinFocusActive) {
            return
        }

        // Skip only fast-forwards the initial all-symbols win clip.
        // Do not dismiss the idle payline loop — startSpin / bet change still stop it.
        if (!this._isWinPresentationActive && this._pendingWinningAnimations <= 0) {
            return
        }

        this._pendingWinningAnimations = 0
        this._isWinPresentationActive = false
        this.forceClearWinAnimations()
    }

    private onBetChanged = () => {
        if (this._isSpinning) {
            return
        }

        const hadWinPresentation = this.hasActiveWinPresentation()

        // Stake change must always clear looping / in-progress line wins.
        this.stopWinLoop()

        if (!hadWinPresentation) {
            return
        }

        this.forceClearWinAnimations()
        this.onIdleState()
    }

    private onChangeDialogState = (isDialogShown: boolean) => {
        if (isDialogShown) {
            this.stopHighPayIdle()
            return
        }

        this.onIdleState()
    }

    private startWinLoop = () => {
        if (!this._winLoopCombinations.length) {
            return
        }

        const currentCombination = this._winLoopCombinations[this._currentWinLoopIndex]

        this.presentLineByLineStepWins(currentCombination)

        this.clearWinLoopTimer()
        this.clearWinLoopResetTimer()
        this._winLoopTimer = setTimeout(() => {
            this.clearLineByLineWins()

            this.clearWinLoopResetTimer()
            this._winLoopResetTimer = setTimeout(() => {
                this._currentWinLoopIndex = (this._currentWinLoopIndex + 1) % this._winLoopCombinations.length
                this.startWinLoop()
            }, ReelsContainer.WIN_LOOP_RESET_MS)
        }, ReelsContainer.WIN_LOOP_SHOW_MS - ReelsContainer.WIN_LOOP_RESET_MS)
    }

    private stopWinLoop = (resetCombinations: boolean = true) => {
        const hadWinPresentation = this.hasActiveWinPresentation()

        this.clearWinLoopTimer()
        this.clearWinLoopResetTimer()

        if (resetCombinations) {
            this._winLoopCombinations = []
            this._currentWinLoopIndex = 0
        }

        this._pendingWinningAnimations = 0
        this._isWinPresentationActive = false
        this._isLineByLineDrivingWins = false

        if (!hadWinPresentation) {
            return
        }

        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                symbol.stopHpOutOverlayAnimation()

                if (this.isHighPaySymbol(symbol.index)) {
                    // Smooth win→idle when line-by-line / win-loop ends (no setup-pose snap).
                    if (symbol.isPlayingLoopingAnimation('win') || symbol.animation) {
                        this.playHighPayIdleSymbol(symbol)
                    }
                } else if (
                    this.shouldKeepIdleAnimation(symbol.index) ||
                    this.isWildSymbol(symbol) ||
                    symbol.animation
                ) {
                    symbol.forceStatic()
                }

                symbol.visible = true
                symbol.shadowOut(false)
            })
        })

        if (this._isFreeSpinsTriggerScatterFocusActive) {
            this.applyFreeSpinsScatterFocus(this._freeSpinsTriggerScatterPositions)
        }
    }

    private clearWinLoopTimer() {
        if (this._winLoopTimer) {
            clearTimeout(this._winLoopTimer)
            this._winLoopTimer = null
        }
    }

    private clearWinLoopResetTimer() {
        if (this._winLoopResetTimer) {
            clearTimeout(this._winLoopResetTimer)
            this._winLoopResetTimer = null
        }
    }

    private playHighPayIdle() {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                if (this.shouldKeepIdleAnimation(symbol.index)) {
                    symbol.shadowOut(false)
                    if (this.isHighPaySymbol(symbol.index)) {
                        this.playHighPayIdleSymbol(symbol)
                    } else {
                        symbol.playAnimation('idle', true)
                    }
                }
            })
        })
    }

    private playHighPayIdleSymbol = (symbol: SymbolContainer): void => {
        if (this._freeSpinsPopupIdleLocks.size > 0) {
            symbol.playAnimation('idle', true)
            this._freeSpinsPopupPausedSymbols.add(symbol)
            symbol.pauseIdleAnimation()
            return
        }

        symbol.playAnimation('idle', true)
    }

    private onFreeSpinsPopupStart = (popup: string): void => {
        // Jackpot / commendation / jackpot-upgraded: freeze board motion behind the overlay.
        this._freeSpinsPopupIdleLocks.add(popup)
        ;(Facade as any).pauseHighPayIdleForFsPopup = true

        // Stop payline win clips (and dim) — line-by-line is also suspended in ShowWinnings.
        this.clearLineByLineWins()

        // Re-scan every start — idle may begin after the first popup signal.
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                if (!this.isHighPaySymbol(symbol.index) || !symbol.animation) {
                    return
                }

                if (symbol.pauseIdleAnimation() || symbol.pauseWinAnimation()) {
                    this._freeSpinsPopupPausedSymbols.add(symbol)
                }
            })
        })
    }

    private onFreeSpinsPopupComplete = (popup: string): void => {
        this._freeSpinsPopupIdleLocks.delete(popup)
        if (this._freeSpinsPopupIdleLocks.size > 0) {
            return
        }

        ;(Facade as any).pauseHighPayIdleForFsPopup = false
        this._freeSpinsPopupPausedSymbols.clear()
        this._isLineByLineDrivingWins = false

        // Commendation: base-game line-by-line may resume on the same signal — wait a tick
        // so we don't start idle underneath the first payline. Otherwise restore HV/MV idle.
        if (popup === 'commendation') {
            queueMicrotask(() => {
                if (this._freeSpinsPopupIdleLocks.size > 0) {
                    return
                }

                if (this._isLineByLineDrivingWins || this._isWinPresentationActive) {
                    return
                }

                this.restoreHighPayIdleAfterPopup()
            })
            return
        }

        this.restoreHighPayIdleAfterPopup()
    }

    private restoreHighPayIdleAfterPopup(): void {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                if (this.isHighPaySymbol(symbol.index)) {
                    this.playHighPayIdleSymbol(symbol)
                }
            })
        })
    }

    private stopHighPayIdle(skipLandingState: boolean = false) {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                if (this.shouldKeepIdleAnimation(symbol.index)) {
                    if (skipLandingState && this.isSymbolPlayingNonLoopLand(symbol)) {
                        return
                    }

                    symbol.stopAnimation()
                }
            })
        })
    }

    private shouldKeepIdleAnimation(index: number): boolean {
        return (
            this.isHighPaySymbol(index) || index === SpecialSymbolType.SCATTER || index === SpecialSymbolType.MULTIPLIER
        )
    }

    private isSymbolPlayingNonLoopLand(symbol: SymbolContainer): boolean {
        const track = (symbol.animation as any)?.state?.tracks?.[0]

        if (!track || track.loop === true) {
            return false
        }

        const animationName = String(track?.animation?.name || '').toLowerCase()
        return animationName.includes('land')
    }

    protected changeSpinSettings(isQuickSpin: boolean) {
        this._speedSettings = isQuickSpin ? Facade.reelsConfig.QUICK : Facade.reelsConfig.NORMAL
    }

    protected onCreateReels = (data: IInitReelsData) => {
        Facade.reelsStates = []
        this._reels = []

        this._reelsContainerBotUnmasked = new PIXI.Container()
        this._reelsContainerBotUnmasked.name = ContainersNames.BOTTOM_UNMASKED_CONTAINER

        this._reelsContainer = new PIXI.Container()
        this._reelsContainer.name = ContainersNames.REELS_CONTAINER

        this._reelsContainerTopUnmasked = new PIXI.Container()
        this._reelsContainerTopUnmasked.name = ContainersNames.TOP_UNMASKED_CONTAINER

        this.addChild(this._reelsContainerBotUnmasked, this._reelsContainer, this._reelsContainerTopUnmasked)

        this.selectReelsStrip(ReelsStripConfig.NORMAL_GAME_SYMBOLS_STRIP)
        this.createSymbolsLayers()

        this._reels = this.createReels(data)

        this.createReelsMask()
        // Do not bind swipe here — createReels runs during load/splash; DOM touches would
        // open the swipe popup on the intro. Wired on completeSplashScreenState instead.
    }

    protected createSymbolsLayers() {
        const layersCount = SymbolLayerType.COUNT
        this._symbolLayers = []

        for (let index = 0; index < layersCount; index++) {
            const layer = new PIXI.Container()
            layer.name = `Layer #${SymbolLayerType[index]}`
            this._symbolLayers.push(layer)

            if (index === SymbolLayerType.UNMASKED_STANDARD_SYMBOLS) {
                this._reelsContainerBotUnmasked.addChild(layer)
            } else if (index > SymbolLayerType.UNMASKED_STANDARD_SYMBOLS) {
                this._reelsContainerTopUnmasked.addChild(layer)
            } else {
                this._reelsContainer.addChild(layer)
            }
        }
    }

    protected createReels(data: IInitReelsData): ReelContainer[] {
        const layersCount = SymbolLayerType.COUNT
        const reels: ReelContainer[] = []

        data.reels.forEach((reel: number[], index: number) => {
            const _reel: ReelContainer = new ReelContainer({
                reelIndex: index,
                reelGrid: data.reels[index],
                originalGrid: data.originalGrid[index],
                activeReelStrip: this._activeReelsStrip[index],
            })

            reels.push(_reel)

            const offset = ReelsContainerHelper.calculateReelWidth() * index

            for (let index = 0; index < layersCount; index++) {
                const layer = _reel.symbolLayers[index]
                layer.x += offset
                this._symbolLayers[index].addChild(layer)
            }
        })

        return reels
    }

    protected createReelsMask() {
        this._cropMask = new PIXI.Graphics()
        this._cropMask.name = ContainersNames.CROP_MASK
        this.redrawCropMask(ReelsContainer.REELS_MASK_DRAW_Y, this.getReelsMaskHeight())
        this._reelsContainer.mask = this._cropMask
        this._cropMask.position.set(ReelsContainer.REELS_MASK_X, ReelsContainer.BASE_GAME_MASK_Y)
        this.addChild(this._cropMask)

        if (ReelsContainer.DEBUG_SHOW_REELS_MASK) {
            this._cropMaskDebug = new PIXI.Graphics()
            this._cropMaskDebug.name = `${ContainersNames.CROP_MASK}_DEBUG`
            this._cropMaskDebug.position.copyFrom(this._cropMask.position)
            this.addChild(this._cropMaskDebug)
            this.redrawCropMaskDebug(ReelsContainer.REELS_MASK_DRAW_Y, this.getReelsMaskHeight())
        }
    }

    private getReelsMaskWidth(): number {
        return ReelsContainer.REELS_MASK_WIDTH
    }

    private getReelsMaskHeight(): number {
        return ReelsContainer.REELS_MASK_HEIGHT
    }

    protected setCropMaskY(y: number) {
        if (!this._cropMask) {
            return
        }

        this._cropMask.y = y
        if (this._cropMaskDebug) {
            this._cropMaskDebug.y = y
        }
    }

    protected redrawCropMask(y: number, height: number) {
        this._cropMask.clear()
        this._cropMask.beginFill(0x542dd, 0.5)
        this._cropMask.drawRect(0, y, this.getReelsMaskWidth(), height)
        this._cropMask.endFill()

        this.redrawCropMaskDebug(y, height)
    }

    private redrawCropMaskDebug(y: number, height: number) {
        if (!this._cropMaskDebug) {
            return
        }

        this._cropMaskDebug.clear()
        this._cropMaskDebug.beginFill(0xff0000, 0.08)
        this._cropMaskDebug.lineStyle(2, 0xff0000, 0.9)
        this._cropMaskDebug.drawRect(0, y, this.getReelsMaskWidth(), height)
        this._cropMaskDebug.endFill()
    }

    private getMobileSwipeSpinConfig() {
        const config = Facade.defaultConfig?.mobileSwipeSpin

        if (typeof config === 'boolean') {
            return {
                enabled: config,
                requireConfirmation: false,
            }
        }

        if (!config || typeof config !== 'object') {
            return {
                enabled: false,
                requireConfirmation: false,
            }
        }

        return {
            enabled: config.enabled !== false,
            minDistance: config.minDistance,
            maxHorizontalDrift: config.maxHorizontalDrift,
            preventDefault: config.preventDefault,
            requireConfirmation: config.requireConfirmation === true,
        }
    }

    private onCompleteSplashScreenStateForSwipe = () => {
        // Wait until DISPLAY_SPLASH has fully transitioned out before binding DOM touches.
        queueMicrotask(() => this.setupMobileSwipeSpin())
    }

    private isSwipeInteractionAllowed(): boolean {
        if (Facade.isPopupBlockingInput) {
            return false
        }

        return !isFeatureRoundActive() && !isSwipeBlockedByGameState()
    }

    private setupMobileSwipeSpin() {
        const config = this.getMobileSwipeSpinConfig()

        this._mobileSwipeSpinController?.destroy()
        this._mobileSwipeSpinController = null

        if (!config.enabled) {
            this._isMobileSwipeSpinFeatureEnabled = false
            this._hasShownMobileSwipeSpinPopup = false
            this.hitArea = null
            return
        }

        this._isMobileSwipeSpinFeatureEnabled = !config.requireConfirmation
        this._hasShownMobileSwipeSpinPopup = false

        this.eventMode = 'static'
        // Large capture zone — DOM canvas listeners are primary; this remains as Pixi backup.
        this.hitArea = new PIXI.Rectangle(-400, -400, 2000, 2000)

        this._mobileSwipeSpinController = new MobileSwipeSpinController(
            this,
            () => {
                if (!this.isSwipeInteractionAllowed()) {
                    return
                }

                if (!this._isMobileSwipeSpinFeatureEnabled) {
                    if (!this._hasShownMobileSwipeSpinPopup) {
                        GameSignals.requestSwipeFeaturePopup.emit()
                    }

                    return
                }

                UIAPI.emit(UIEvent.clicked_play)
            },
            config
        )
    }

    private onEnableSwipeFeature = (enabled: boolean = false) => {
        this._isMobileSwipeSpinFeatureEnabled = enabled === true
    }

    private onSwipeFeaturePopupShown = () => {
        this._hasShownMobileSwipeSpinPopup = true
    }

    protected selectReelsStrip(reelsStrip: number[][]) {
        this._activeReelsStrip = reelsStrip

        for (let index = 0; index < this._reels.length; index++) {
            this._reels[index].selectReelsStrip(reelsStrip[index])
        }
    }

    protected changeMaskParams = (state: string) => {
        const spinMaskHeight = this.getReelsMaskHeight()

        switch (state) {
            case 'start':
                this.redrawCropMask(ReelsContainer.SPIN_MASK_DRAW_Y, spinMaskHeight)
                break
            case 'stop':
                this.redrawCropMask(ReelsContainer.SPIN_MASK_DRAW_Y, spinMaskHeight)
                break
        }
    }

    protected onStartSpin = () => {
        this.pruneFsCoinCollectAnimations()
        this.clearFsCollectCoinFocus(false)

        // Cancel feature-intro fade so the first FS spin is visible immediately.
        gsap.killTweensOf(this)
        this.alpha = 1

        if (Facade.model?.spinState?.isHoldAndWinActive) {
            this.visible = false
            this._isSpinning = false
            this.clearReelsStopTimers()
            return
        }

        this.visible = true
        this.setAnticipationDimmingMode(false)
        this._isCoinRespinAnticipationVisible = false
        this._isScatterAnticipationVisible = false

        if (this._isSpinning) {
            return
        }

        const needsHardWinClear = this.hasActiveWinPresentation()
        this.stopWinLoop()
        if (needsHardWinClear) {
            this.forceClearWinAnimations()
        }
        this.stopHighPayIdle()
        this.clearReelsStopTimers()

        this._isQuickStop = Facade.isTurboSpin
        this._isSpinning = true
        this._scattersCount = 0
        this._immediatelyStopped = false
        // this._isAnticipationVisible = false
        this._cascadeIndex = 0

        this.changeSpinSettings(this._isQuickStop)
        this.changeMaskParams('start')

        this._reels.forEach((reel: ReelContainer, index: number) => {
            reel.reelGrid = null
            reel.originalReelGrid = null
            reel.start()
        })
    }

    protected onReelJustTouched = (index: number, winningsSymbolsIndexes: number[]) => {
        const reel: ReelContainer = this._reels[index]
        const isCoinAnticipationVisible = this._isCoinRespinAnticipationVisible
        const isScatterAnticipationVisible = this._isScatterAnticipationVisible
        const isAnyAnticipationVisible = isCoinAnticipationVisible || isScatterAnticipationVisible
        const highlightedSymbolIndex = isCoinAnticipationVisible
            ? SpecialSymbolType.MULTIPLIER
            : isScatterAnticipationVisible
              ? SpecialSymbolType.SCATTER
              : null
        let symbols: SymbolContainer[]

        if (winningsSymbolsIndexes) {
            symbols = []
            winningsSymbolsIndexes.forEach((symbolIndex: number) => {
                symbols.push(reel.symbols[symbolIndex])
            })
        } else {
            const extraTop = Facade.reelsConfig.EXTRA_SYMBOLS_TOP
            const extraBottom = Facade.reelsConfig.EXTRA_SYMBOLS_BOTTOM
            const toIndex = Math.max(extraTop, reel.symbols.length - extraBottom)
            symbols = reel.symbols.slice(extraTop, toIndex)
        }

        for (const symbol of symbols) {
            switch (symbol.index) {
                case SpecialSymbolType.SCATTER:
                    this._scattersCount++
                    GameSignals.scatterLanded.emit(this._scattersCount, index)

                    reel.changeSymbolLayer(symbol, true)

                    const neededScattersCount: number = Facade.model.roundState.isFreeSpins ? 1 : 2

                    if (this._scattersCount > neededScattersCount) {
                        for (const reel of this._reels) {
                            gsap.to(reel, {
                                duration: 0.7,
                                onComplete: () => {
                                    if (reel.reelState === reel.reelStates.STOPPED) {
                                        reel.playScatterWaiting(reel.index)
                                    }
                                },
                            })
                        }
                    }
                    if (!isAnyAnticipationVisible) {
                        symbol.shadowOut(false)
                    }
                    symbol.playAnimation(['land', 'idle'], [false, true])
                    break
                case SpecialSymbolType.MULTIPLIER:
                    GameSignals.wildLanded.emit()
                    GameSignals.playMultiplierLandSound.emit(symbol.data.multiplier)
                    ;(GameSignals as any).baseGameCoinLanded?.emit({
                        reelIndex: index,
                        multiplier: Number(symbol.data?.multiplier || 0),
                        coinType: String(symbol.data?.coinType || ''),
                        symbolKey: String(symbol.data?.symbolKey || ''),
                        symbolIndex: Number(symbol.index),
                    })
                    reel.changeSymbolLayer(symbol, true)
                    symbol.shadowOut(false)

                    symbol.playAnimation(['land', 'idle'], [false, true], null, true)
                    break
            }

            if (this.isHighPaySymbol(symbol.index)) {
                if (!isAnyAnticipationVisible) {
                    symbol.shadowOut(false)
                }
                symbol.playAnimation(['land', 'idle'], [false, true])
                continue
            }

            if (this.isWildSymbol(symbol)) {
                continue
            }

            if (this.isLowPaySymbol(symbol.index)) {
                if (!isAnyAnticipationVisible) {
                    symbol.shadowOut(false)
                }
                symbol.playAnimation('land')
            }

            if (isAnyAnticipationVisible) {
                if (
                    symbol.index === highlightedSymbolIndex ||
                    symbol.index === SpecialSymbolType.SCATTER ||
                    symbol.index === SpecialSymbolType.MULTIPLIER
                ) {
                    symbol.shadowOut(false)
                } else {
                    symbol.shadowIn(false, ReelsContainer.COIN_ANTICIPATION_DIM_POWER)
                }
            }
        }
    }

    private isLowPaySymbol(symbolIndex: number): boolean {
        return (
            symbolIndex === SymbolType.LP_1 ||
            symbolIndex === SymbolType.LP_2 ||
            symbolIndex === SymbolType.LP_3 ||
            symbolIndex === SymbolType.LP_4 ||
            symbolIndex === SymbolType.LP_5
        )
    }

    protected onSpinStoping = (data: IStoppingReelsData) => {
        if (data.isHoldAndWinRespin) {
            this.visible = false
            this._isSpinning = false
            this._isCoinRespinAnticipationActive = false
            this._isScatterAnticipationActive = false
            this._isCoinRespinAnticipationVisible = false
            this._isScatterAnticipationVisible = false
            this.setAnticipationDimmingMode(false)
            this.clearReelsStopTimers()
            this._pendingAnticipationReels.clear()
            this._anticipationPerReelDelayMs = 0
            return
        }

        this.captureBaseGameGridSnapshot(data)

        this.visible = true
        this.restoreBaseSymbolsVisualState()
        this._anticipationIndices = data.anticipationIndices
        this._isCoinRespinAnticipationActive =
            !data.isScatterAnticipation && !data.isHoldAndWinRespin && this._anticipationIndices.length > 0
        this._isScatterAnticipationActive =
            Boolean(data.isScatterAnticipation) && !data.isHoldAndWinRespin && this._anticipationIndices.length > 0
        this._isCoinRespinAnticipationVisible = false
        this._isScatterAnticipationVisible = false
        this.setAnticipationDimmingMode(false)
        this.clearReelsStopTimers()

        this._reels.forEach((reel: ReelContainer, index: number) => {
            reel.setData(data.reels[index], data.originalGrid[index], this._isQuickStop)
        })

        this.changeMaskParams('stop')

        const reelsConfig = Facade.reelsConfig
        const configuredAnticipationDelay =
            (data.isScatterAnticipation ? reelsConfig.SCATTER_SUSPENSE_TIME : reelsConfig.FEATURE_SUSPENSE_TIME) * 1000
        const anticipationDelay = configuredAnticipationDelay * ReelsContainer.ANTICIPATION_DELAY_SCALE
        const perReelAnticipationDuration = Math.min(
            anticipationDelay,
            data.isScatterAnticipation
                ? ReelsContainer.SCATTER_ANTICIPATION_PER_REEL_MS
                : ReelsContainer.FEATURE_ANTICIPATION_PER_REEL_MS
        )
        const stopDelayBetweenReels =
            reelsConfig.DELAY_BETWEEN_REELS ?? this._speedSettings.DELAY_BETWEEN_REELS_ON_STOPPING ?? 0
        const reelsDelay = (this._isQuickStop ? 0 : stopDelayBetweenReels) * 1000
        const firstAnticipationIndex =
            this._anticipationIndices.length && !this._immediatelyStopped
                ? Math.min(...this._anticipationIndices)
                : -1

        //## From the first suspense reel on, reels are released by the onReelStopped chain rather
        //## than by an absolute timer, and only the anticipating ones wait the suspense delay — the
        //## others keep the spin's own cascade, which is 0 under turbo. Pre-scheduling them instead
        //## made a reel sitting after a suspense one land while that one was still building up,
        //## since its timer was measured from the stop and knew nothing about the suspense wait.
        this._pendingAnticipationReels = new Set(
            firstAnticipationIndex >= 1
                ? this._reels.map((_, index) => index).filter((index) => index >= firstAnticipationIndex)
                : []
        )
        this._anticipationPerReelDelayMs = perReelAnticipationDuration
        this._chainedReelStopDelayMs = reelsDelay

        let baseStopTime = 0

        this._reels.forEach((reel: ReelContainer, index: number) => {
            baseStopTime += reelsDelay

            if (this._pendingAnticipationReels.has(index)) {
                return
            }

            const stopTime = baseStopTime
            const stopTimer = setTimeout(() => {
                reel.stoppingReel(false, false)

                if (reel.index === Facade.reelsConfig.TOTAL_REELS - 1) {
                    this._isSpinning = false
                }
            }, stopTime)

            this._reelsStopTimers.push(stopTimer)
        })
    }

    private onReelStopped = (reelIndex: number) => {
        const isLastReel = Number(reelIndex) === Facade.reelsConfig.TOTAL_REELS - 1
        if (isLastReel) {
            this._isSpinning = false
            this._pendingAnticipationReels.clear()
        }

        if (!this._pendingAnticipationReels.size || this._immediatelyStopped) {
            return
        }

        const nextReelIndex = Number(reelIndex) + 1
        if (!this._pendingAnticipationReels.has(nextReelIndex)) {
            return
        }

        this._pendingAnticipationReels.delete(nextReelIndex)

        const isSuspenseReel = this._anticipationIndices.includes(nextReelIndex)
        const stopDelay = Math.max(
            0,
            isSuspenseReel ? this._anticipationPerReelDelayMs : this._chainedReelStopDelayMs
        )
        const timerId = setTimeout(() => {
            const reel = this._reels[nextReelIndex]
            if (!reel) {
                return
            }

            reel.stoppingReel(isSuspenseReel, false)
        }, stopDelay)

        this._reelsStopTimers.push(timerId)
    }

    protected onImmediateStop = () => {
        if (this._immediatelyStopped) {
            return
        }

        this._isQuickStop = true
        this._immediatelyStopped = true
        this._isCoinRespinAnticipationActive = false
        this._isScatterAnticipationActive = false
        this._isCoinRespinAnticipationVisible = false
        this._isScatterAnticipationVisible = false
        this.setAnticipationDimmingMode(false)
        this._pendingAnticipationReels.clear()
        this._anticipationPerReelDelayMs = 0
        GameSignals.reelsImmediatelyStopped.emit()

        const hasStopData = this._reels.every(
            (reel: ReelContainer) => Array.isArray(reel.reelGrid) && Array.isArray(reel.originalReelGrid)
        )

        if (!hasStopData) {
            return
        }

        gsap.killTweensOf(this._reels)
        this.clearReelsStopTimers()
        this.changeMaskParams('stop')

        this._reels.forEach((reel: ReelContainer) => {
            if (reel.reelState !== reel.reelStates.STOPPING && reel.reelState !== reel.reelStates.STOPPED) {
                reel.stoppingReel(false, true)
                // if (this._anticipationIndices.length && this._anticipationIndices.includes(reel.index) && this._isAnticipationVisible) {
                //     this.symbolsShadowIn([SpecialSymbolType.SCATTER], false)
                // }
            }
        })

        this._isSpinning = false
    }

    private clearReelsStopTimers() {
        this._reelsStopTimers.forEach((timerId) => {
            clearTimeout(timerId)
        })

        this._reelsStopTimers = []
    }

    protected onErrorStop = (data: IStoppingReelsData) => {
        this._isQuickStop = true
        this._isCoinRespinAnticipationActive = false
        this._isScatterAnticipationActive = false
        this._isCoinRespinAnticipationVisible = false
        this._isScatterAnticipationVisible = false
        this.setAnticipationDimmingMode(false)
        this._pendingAnticipationReels.clear()
        this._anticipationPerReelDelayMs = 0
        if (this._isSpinning) {
            this.onSpinStoping(data)
        }
    }

    private onShowSuspenseAnimation = () => {
        const highlightedSymbolIndex = this.getAnticipationHighlightedSymbolIndex()
        if (highlightedSymbolIndex === null) {
            return
        }

        this._isCoinRespinAnticipationVisible = true
        this._isScatterAnticipationVisible = true
        this.setAnticipationDimmingMode(true, highlightedSymbolIndex)
        this.applyAnticipationClassicDimming(highlightedSymbolIndex)
    }

    private onHideSuspenseAnimation = () => {
        if (this._isHoldAndWinTriggerCoinWinActive) {
            return
        }

        if (!this._isCoinRespinAnticipationVisible && !this._isScatterAnticipationVisible) {
            return
        }

        if (this._isFreeSpinsTriggerScatterFocusActive) {
            this.setAnticipationDimmingMode(false)
            this._isCoinRespinAnticipationActive = false
            this._isCoinRespinAnticipationVisible = false
            this._isScatterAnticipationActive = false
            this._isScatterAnticipationVisible = false
            this.applyFreeSpinsScatterFocus(this._freeSpinsTriggerScatterPositions)
            return
        }

        this.clearAnticipationClassicDimming()
        this.setAnticipationDimmingMode(false)
        this._isCoinRespinAnticipationActive = false
        this._isCoinRespinAnticipationVisible = false
        this._isScatterAnticipationActive = false
        this._isScatterAnticipationVisible = false
    }

    private getAnticipationHighlightedSymbolIndex(): number | null {
        if (this._isCoinRespinAnticipationActive) {
            return SpecialSymbolType.MULTIPLIER
        }

        if (this._isScatterAnticipationActive) {
            return SpecialSymbolType.SCATTER
        }

        return null
    }

    private setAnticipationDimmingMode(active: boolean, highlightedSymbolIndex: number | null = null): void {
        this._reels.forEach((reel: ReelContainer) => {
            reel.setAnticipationDimming(active, ReelsContainer.COIN_ANTICIPATION_DIM_POWER, highlightedSymbolIndex)
        })
    }

    private applyAnticipationClassicDimming(highlightedSymbolIndex: number): void {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                if (
                    symbol.index === highlightedSymbolIndex ||
                    symbol.index === SpecialSymbolType.SCATTER ||
                    symbol.index === SpecialSymbolType.MULTIPLIER
                ) {
                    symbol.shadowOut(false)
                    return
                }

                symbol.shadowIn(false, ReelsContainer.COIN_ANTICIPATION_DIM_POWER)
            })
        })
    }

    private clearAnticipationClassicDimming(): void {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                symbol.shadowOut(false)
            })
        })
    }

    protected symbolsShadowIn = (exceptions: number[], animate: boolean = true) => {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                if (exceptions.length && exceptions.includes(symbol.index)) {
                    return
                }

                symbol.shadowIn(animate)
            })
        })
    }

    protected symbolsShadowOut = () => {
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                symbol.shadowOut(true)
            })
        })
    }

    private restoreBaseSymbolsVisualState = () => {
        if (this._isFsCollectCoinFocusActive) {
            this._isFsCollectCoinFocusApplied = false
        }

        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                symbol.visible = true
                symbol.shadowOut(false)
            })
        })

        this.reapplyTransientSymbolFocus()
    }

    private isBaseRevealBlocked = (): boolean => {
        const isHoldAndWinStillActive = Boolean(Facade.model?.spinState?.isHoldAndWinActive)
        let isHoldAndWinPostSequenceActive = Boolean(
            (Facade as any).holdAndWinCollectInProgress || (Facade as any).holdAndWinPendingBaseTransition
        )

        // Recover from stale post-sequence flags left behind by interrupted/restore flows.
        if (isHoldAndWinPostSequenceActive && !isHoldAndWinStillActive) {
            ;(Facade as any).holdAndWinCollectInProgress = false
            ;(Facade as any).holdAndWinPendingBaseTransition = false
            isHoldAndWinPostSequenceActive = false
        }

        return isHoldAndWinStillActive || isHoldAndWinPostSequenceActive
    }

    private onHoldAndWinStop = () => {
        this.clearFsCoinCollectEffects()
        this._isHoldAndWinTriggerCoinWinActive = false
        this.visible = true
        this.alpha = 1
        this.restoreBaseSymbolsVisualState()
    }

    private onFeatureTransitionStart = () => {
        this._isHoldAndWinTriggerCoinWinActive = false
        gsap.killTweensOf(this)
        gsap.to(this, {
            duration: 0.35,
            alpha: 0,
        })
    }

    private onFeatureIntroContinue = () => {
        gsap.killTweensOf(this)
        // Instant reveal — a 0.25s fade made the first FS/H&W spin feel delayed after Congrats.
        this.alpha = 1
        this.visible = true
    }

    private onHoldAndWinTriggerCoinWinStart = () => {
        this._isHoldAndWinTriggerCoinWinActive = true
        this.stopWinLoop()
        this.stopHighPayIdle(true)

        const triggerCoins: SymbolContainer[] = []
        const multiplierCoinsFallback: SymbolContainer[] = []

        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                if (symbol.index !== SpecialSymbolType.MULTIPLIER) {
                    return
                }

                multiplierCoinsFallback.push(symbol)

                const coinType = String(symbol.data?.coinType || '').toLowerCase()
                const symbolKey = String(symbol.data?.symbolKey || '').toLowerCase()
                const isTriggerCoin =
                    coinType === 'mini' ||
                    coinType === 'minor' ||
                    coinType === 'major' ||
                    coinType === 'grand' ||
                    coinType === 'prize' ||
                    symbolKey === 'k'

                if (!isTriggerCoin) {
                    return
                }

                triggerCoins.push(symbol)
            })
        })

        const targetCoins = triggerCoins.length ? triggerCoins : multiplierCoinsFallback

        if (!targetCoins.length) {
            ;(GameSignals as any).holdAndWinTriggerCoinWinComplete?.emit()
            return
        }

        this.visible = true
        this.alpha = 1
        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                if (symbol.index === SpecialSymbolType.MULTIPLIER) {
                    symbol.shadowOut(false)
                    return
                }

                symbol.shadowIn(false)
            })
        })

        let pending = targetCoins.length
        let isCompleted = false

        const completeSequence = () => {
            if (isCompleted) {
                return
            }

            isCompleted = true
            ;(GameSignals as any).holdAndWinTriggerCoinWinComplete?.emit()
        }

        gsap.delayedCall(ReelsContainer.HOLD_AND_WIN_TRIGGER_ANIMATION_FALLBACK_MS / 1000, completeSequence)

        targetCoins.forEach((symbol: SymbolContainer) => {
            symbol.playAnimation(
                'win',
                false,
                () => {
                    pending--
                    if (pending <= 0) {
                        completeSequence()
                    }
                },
                true
            )
        })
    }

    protected onActivateScatters = (data: FreeSpinsActivateData) => {
        // Only bonus scatters (and prize coins on dual trigger) should animate — not regular win symbols.
        this.freezeBoardForScatterTrigger()

        const scatterPositions = Array.isArray(data?.symbolsPositions) ? data.symbolsPositions : []
        this.applyFreeSpinsScatterFocus(scatterPositions)
        this._isFreeSpinsTriggerScatterFocusActive = true
        this._freeSpinsTriggerScatterPositions = scatterPositions
        this.clearPendingFreeSpinsTriggerScatterFocusRestore()

        const coinCountInRound = Number((Facade.model?.spinData?.bonus as any)?.num_coin_symbols || 0)
        if (coinCountInRound >= 6) {
            this.playPrizeCoinTriggerAnimations()
        }

        for (const reel of this._reels) {
            reel.playScatterTriggerAnimation()
        }

        // Keep non-scatter dimming until the feature flow explicitly releases it.
    }

    /** Stop win/idle motion so FS trigger only shows scatter (and optional prize-coin) clips. */
    private freezeBoardForScatterTrigger(): void {
        this.clearWinLoopTimer()
        this.clearWinLoopResetTimer()
        this._winLoopCombinations = []
        this._currentWinLoopIndex = 0
        this._pendingWinningAnimations = 0
        this._isWinPresentationActive = false
        this._isLineByLineDrivingWins = false
        this.stopHighPayIdle()

        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                if (symbol.index === SpecialSymbolType.SCATTER) {
                    return
                }

                symbol.stopHpOutOverlayAnimation()

                if (symbol.index === SpecialSymbolType.MULTIPLIER) {
                    if (symbol.animation) {
                        symbol.forceStatic()
                    }
                    return
                }

                if (this.isHighPaySymbol(symbol.index) && symbol.animation) {
                    symbol.freezeAtIdle()
                    return
                }

                if (symbol.animation || this.canPlayWinAnimation(symbol)) {
                    symbol.forceStatic()
                }
            })
        })
    }

    private clearPendingFreeSpinsTriggerScatterFocusRestore(): void {
        if (!this._freeSpinsTriggerScatterFocusRestoreTween) {
            return
        }

        this._freeSpinsTriggerScatterFocusRestoreTween.kill()
        this._freeSpinsTriggerScatterFocusRestoreTween = null
    }

    private clearFreeSpinsTriggerScatterFocus(animate: boolean): void {
        this.clearPendingFreeSpinsTriggerScatterFocusRestore()

        if (!this._isFreeSpinsTriggerScatterFocusActive) {
            this._freeSpinsTriggerScatterPositions = []
            this.reapplyTransientSymbolFocus()
            return
        }

        this._isFreeSpinsTriggerScatterFocusActive = false
        this._freeSpinsTriggerScatterPositions = []

        this._reels.forEach((reel: ReelContainer) => {
            reel.symbols.forEach((symbol: SymbolContainer) => {
                symbol.shadowOut(animate)
            })
        })

        this.reapplyTransientSymbolFocus()
    }

    private reapplyTransientSymbolFocus(): void {
        if (this._isFreeSpinsTriggerScatterFocusActive) {
            this.applyFreeSpinsScatterFocus(this._freeSpinsTriggerScatterPositions)
            return
        }

        if (this._isFsCollectCoinFocusActive) {
            this.applyFsCollectCoinFocus()
        }
    }

    private applyFreeSpinsScatterFocus(scatterPositions: number[][]): void {
        const scatterSet = new Set(scatterPositions.map((position: number[]) => `${position[0]}:${position[1]}`))
        const extraTop = Number(Facade.reelsConfig.EXTRA_SYMBOLS_TOP || 0)
        const visibleRows = Number(Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL || 0)

        this._reels.forEach((reel: ReelContainer, reelIndex: number) => {
            reel.symbols.forEach((symbol: SymbolContainer, symbolIndex: number) => {
                const visibleRowIndex = symbolIndex - extraTop
                const isVisibleRow = visibleRowIndex >= 0 && visibleRowIndex < visibleRows

                if (!isVisibleRow) {
                    return
                }

                const positionKey = `${reelIndex}:${visibleRowIndex}`
                const isScatterSymbol = symbol.index === SpecialSymbolType.SCATTER || scatterSet.has(positionKey)

                if (isScatterSymbol) {
                    symbol.shadowOut(false)
                } else {
                    symbol.shadowIn(false, ReelsContainer.FREE_SPINS_SCATTER_FOCUS_DIM_POWER)
                }
            })
        })
    }

    protected onFreeSpinsIntro = () => {
        this.clearFreeSpinsTriggerScatterFocus(false)
        this.stopWinLoop()
        this.stopHighPayIdle()
        this.setCropMaskY(ReelsContainer.FREE_SPINS_MASK_Y)
        this.selectReelsStrip(ReelsStripConfig.FREE_GAME_SYMBOLS_STRIP)
    }

    protected onFreeSpinsComplete = () => {
        this.clearFreeSpinsTriggerScatterFocus(false)
        this.clearFsCollectCoinFocus(false)
        this.stopWinLoop()
        this.stopHighPayIdle()
        this.clearFsCoinCollectEffects()
        this.setCropMaskY(ReelsContainer.BASE_GAME_MASK_Y)
        this.selectReelsStrip(ReelsStripConfig.NORMAL_GAME_SYMBOLS_STRIP)
    }

    /** Keeps the last base-game combination, i.e. the one that triggered the feature. */
    private captureBaseGameGridSnapshot = (data: IStoppingReelsData) => {
        if (Facade.model?.roundState?.isFreeSpins) {
            return
        }

        if (!Array.isArray(data?.reels) || !data.reels.length) {
            return
        }

        this._baseGameGridSnapshot = {
            reels: data.reels.map((reel: number[]) => [...(reel ?? [])]),
            originalGrid: (data.originalGrid ?? []).map((reel: SymbolData[]) =>
                (reel ?? []).map((symbolData: SymbolData) => ({ ...symbolData }))
            ),
        }
    }

    private restoreBaseGameGridSnapshot = () => {
        const snapshot = this._baseGameGridSnapshot

        if (!snapshot || !this._reels?.length || Facade.model?.roundState?.isFreeSpins) {
            return
        }

        this.stopWinLoop()
        this.stopHighPayIdle(true)

        this.setAnticipationDimmingMode(false)

        this._reels.forEach((reel: ReelContainer, index: number) => {
            const reelGrid = snapshot.reels[index]

            if (!reelGrid) {
                return
            }

            reel.applyStaticGrid(reelGrid, snapshot.originalGrid[index] ?? [])
        })

        this.restoreBaseSymbolsVisualState()
    }

    protected onMultiplierState = (lastGridData: SymbolData[][]) => {
        const multiplierEntries: Array<{ reelIndex: number; rowIndex: number; multiplier: number }> = []

        lastGridData.forEach((reelData: SymbolData[], reelIndex: number) => {
            reelData.forEach((symbolData: SymbolData, rowIndex: number) => {
                if (symbolData?.id === SpecialSymbolType.MULTIPLIER) {
                    multiplierEntries.push({
                        reelIndex,
                        rowIndex,
                        multiplier: Number(symbolData?.multiplier) || 0,
                    })
                }
            })
        })

        let delay: number = 0

        multiplierEntries.forEach((entry, index, array) => {
            const symbol = this.getVisibleReelSymbol(entry.reelIndex, entry.rowIndex)
            const lastPosition: boolean = index === array.length - 1

            if (!symbol) {
                lastPosition
                    ? GameSignals.multiplierLanded.emit(entry.multiplier)
                    : GameSignals.updateJarValue.emit(entry.multiplier, true)
                delay += Facade.reelsConfig.DELAY_PER_MULTIPLIER_SYMBOLS
                return
            }

            gsap.to(symbol, {
                duration: delay,
                onComplete: () => {
                    symbol.showMultiplierLandAnimation(() => {
                        const emittedMultiplier = Number(symbol.data?.multiplier) || entry.multiplier

                        const continueFlow = () => {
                            lastPosition
                                ? GameSignals.multiplierLanded.emit(emittedMultiplier)
                                : GameSignals.updateJarValue.emit(emittedMultiplier, true)
                        }

                        if (this.isFsCollectAllowed()) {
                            const p = symbol.getGlobalPosition(new PIXI.Point())
                            GameSignals.fsTopMultiplierCollectFly.emit({
                                fromX: p.x,
                                fromY: p.y,
                                multiplier: emittedMultiplier,
                            })

                            this.playFsCoinCollectToActiveLadder(symbol, continueFlow)
                            return
                        }

                        continueFlow()
                    })
                },
            })
            delay += Facade.reelsConfig.DELAY_PER_MULTIPLIER_SYMBOLS
        })
    }

    private onFsCollectSequenceRequested = () => {
        this._isFsCollectFastForward = false

        if (!this.isFsCollectAllowed()) {
            this.clearFsCollectCoinFocus(false)
            ;(GameSignals as any).fsCollectSequenceComplete?.emit()
            return
        }

        // Only the coins the ladder can still take are collected. Anything landing past the last
        // level stays on the reels and is worth 0.
        const collectCapacity = getLadderRemainingCoinCapacity()
        this.captureFsCollectFinalLadderState()
        this.syncRuntimeFsLadderMilestonesFromModel()

        const multiplierSymbols = this.getVisibleMultiplierSymbols().slice(0, Math.max(0, collectCapacity))
        if (!multiplierSymbols.length) {
            this.clearFsCollectCoinFocus(false)
            ;(GameSignals as any).fsCollectSequenceComplete?.emit()
            return
        }

        this.stopWinLoop()
        this._isFsCollectCoinFocusActive = true
        this.applyFsCollectCoinFocus()
        this._pendingFsCollectCount = multiplierSymbols.length
        this._fsCollectExpectedHits = multiplierSymbols.length
        this._fsCollectProcessedHits = 0
        this._pendingFsCollectMultipliers = multiplierSymbols.map((symbol) => Number(symbol.data?.multiplier) || 0)
        Facade.isPopupBlockingInput = true

        this.startFsCollectSequence(multiplierSymbols)
    }

    private startFsCollectSequence(symbols: SymbolContainer[]): void {
        if (!symbols.length) {
            this._pendingFsCollectCount = 0
            this._fsCollectExpectedHits = 0
            this._fsCollectProcessedHits = 0
            Facade.isPopupBlockingInput = false
            ;(GameSignals as any).fsCollectSequenceComplete?.emit()
            return
        }

        const targetLevelIndices = this.assignFsCollectTargetLevels(symbols.length)

        symbols.forEach((symbol, index) => {
            const collectDelay = gsap.delayedCall(index * 0.08, () => {
                if (!symbol || symbol.destroyed) {
                    this.onSingleFsCollectComplete(0)
                    return
                }

                const emittedMultiplier = Number(symbol.data?.multiplier) || 0
                const p = symbol.getGlobalPosition(new PIXI.Point())
                GameSignals.fsTopMultiplierCollectFly.emit({
                    fromX: p.x,
                    fromY: p.y,
                    multiplier: emittedMultiplier,
                })

                this.playFsCoinCollectToActiveLadder(
                    symbol,
                    () => this.onSingleFsCollectComplete(emittedMultiplier),
                    targetLevelIndices[index]
                )
            })

            this.trackFsCollectAnimation(collectDelay)
        })
    }

    private onSingleFsCollectComplete = (multiplier: number = 0) => {
        this.pruneFsCoinCollectAnimations()

        const pendingMultiplierIndex = this._pendingFsCollectMultipliers.indexOf(multiplier)
        if (pendingMultiplierIndex >= 0) {
            this._pendingFsCollectMultipliers.splice(pendingMultiplierIndex, 1)
        }

        this._fsCollectProcessedHits++
        const isLastHit = this._fsCollectExpectedHits > 0 && this._fsCollectProcessedHits >= this._fsCollectExpectedHits
        const normalizedMultiplier = Number.isFinite(multiplier) ? multiplier : 0

        if (!isLastHit || !this.applyFsCollectFinalLadderState()) {
            this.advanceRuntimeFsLadderMilestone()
        }

        // Ladder values/levels must update exactly when each ball reaches the ladder.
        if (isLastHit) {
            GameSignals.multiplierLanded.emit(normalizedMultiplier)
        } else {
            GameSignals.updateJarValue.emit(normalizedMultiplier, true)
        }

        this._pendingFsCollectCount = Math.max(0, this._pendingFsCollectCount - 1)

        if (this._pendingFsCollectCount === 0) {
            this._fsCollectExpectedHits = 0
            this._fsCollectProcessedHits = 0
            this._isFsCollectFastForward = false
            this._fsCollectFinalLadderCount = -1
            this._fsCollectFinalMilestones = []
            Facade.isPopupBlockingInput = false
            ;(GameSignals as any).fsCollectSequenceComplete?.emit()
        }
    }

    private onFsCollectSequenceSkip = (): void => {
        if (!this._pendingFsCollectCount) {
            return
        }

        // Skip only speeds the coin → ladder flies. Never hide them on tap.
        this._isFsCollectFastForward = true
        this._fsCoinCollectGlowTweens.forEach((animation) => {
            animation.timeScale(ReelsContainer.FS_COIN_COLLECT_SKIP_TIME_SCALE)
        })
    }

    private finishFsCollectSequenceNow = (): void => {
        const remaining = this._pendingFsCollectCount
        if (remaining <= 0) {
            return
        }

        const leftoverMultipliers = this._pendingFsCollectMultipliers.slice()
        this.killFsCollectAnimations()

        for (let index = 0; index < remaining; index++) {
            this.onSingleFsCollectComplete(leftoverMultipliers[index] ?? 0)
        }
    }

    private playPrizeCoinTriggerAnimations(): void {
        const extraTop = Number(Facade.reelsConfig.EXTRA_SYMBOLS_TOP || 0)
        const extraBottom = Number(Facade.reelsConfig.EXTRA_SYMBOLS_BOTTOM || 0)

        this._reels.forEach((reel: ReelContainer) => {
            const toIndex = Math.max(extraTop, reel.symbols.length - extraBottom)
            for (let symbolIndex = extraTop; symbolIndex < toIndex; symbolIndex++) {
                const symbol = reel.symbols[symbolIndex]
                if (symbol?.index !== SpecialSymbolType.MULTIPLIER) {
                    continue
                }

                const coinType = String(symbol.data?.coinType || '').toLowerCase()
                const symbolKey = String(symbol.data?.symbolKey || '').toLowerCase()
                if (coinType !== 'prize' && symbolKey !== 'k') {
                    continue
                }

                reel.changeSymbolLayer(symbol, true)
                symbol.playAnimation('win', false, null, true)
            }
        })
    }

    private playFsCoinCollectToActiveLadder(
        symbol: SymbolContainer,
        onComplete?: () => void,
        targetLevelIndex?: number
    ): void {
        const root = Facade.container as PIXI.Container | null
        if (!root) {
            onComplete?.()
            return
        }

        const ladderContainer = this.findLadderContainerFromRoots()
        const assignedTarget =
            targetLevelIndex === undefined ? null : this.getLadderTargetByIndex(ladderContainer, targetLevelIndex)
        const resolvedTarget =
            assignedTarget || getJackpotLadderCollectTarget() || this.getActiveLadderTarget(ladderContainer)
        if (!resolvedTarget) {
            onComplete?.()
            return
        }

        const overlayParent = (resolvedTarget.parent as PIXI.Container) || root
        const fromGlobal = symbol.getGlobalPosition(new PIXI.Point())
        const fromLocal = overlayParent.toLocal(fromGlobal)
        const targetLocal = overlayParent.toLocal(resolvedTarget.getGlobalPosition(new PIXI.Point()))

        const glow = new SpineAnimation()
        glow.name = 'FS Coin Collect Glow'
        glow.init({ name: 'jackpotLadderAnimation' })

        if (!glow.animation) {
            glow.destroy({ children: true })
            onComplete?.()
            return
        }

        glow.position.set(fromLocal.x, fromLocal.y)
        const startScale =
            ReelsContainer.FS_COIN_COLLECT_GLOW_SCALE * ReelsContainer.FS_COIN_COLLECT_GLOW_ON_COIN_SCALE_FACTOR
        glow.scale.set(startScale, startScale)
        overlayParent.addChild(glow)
        this._fsCoinCollectGlowSpines.push(glow)
        glow.start([ReelsContainer.FS_COIN_COLLECT_GLOW_NAME], [true], 0, [1])

        const holdTween = gsap.delayedCall(ReelsContainer.FS_COIN_COLLECT_HOLD_SECONDS, () => {
            symbol.shadowIn(false, ReelsContainer.FS_COLLECTED_COIN_DIM_POWER)
            ;(GameSignals as any).fsCoinCollectMoveToLadder?.emit()

            const flyTween = gsap.timeline({
                onComplete: () => {
                    this.destroyFsCoinCollectGlow(glow)
                    onComplete?.()
                },
            })

            flyTween.to(
                glow,
                {
                    duration: ReelsContainer.FS_COIN_COLLECT_FLY_SECONDS,
                    ease: 'power2.inOut',
                    x: targetLocal.x,
                    y: targetLocal.y,
                },
                0
            )
            flyTween.to(
                glow.scale,
                {
                    duration: ReelsContainer.FS_COIN_COLLECT_FLY_SECONDS,
                    ease: 'power2.inOut',
                    x: ReelsContainer.FS_COIN_COLLECT_TARGET_SCALE,
                    y: ReelsContainer.FS_COIN_COLLECT_TARGET_SCALE,
                },
                0
            )

            this.trackFsCollectAnimation(flyTween)
        })

        this.trackFsCollectAnimation(holdTween)
    }

    private destroyFsCoinCollectGlow(glow: SpineAnimation): void {
        this._fsCoinCollectGlowSpines = this._fsCoinCollectGlowSpines.filter((item) => item !== glow)

        try {
            glow.stop()
        } catch {
            // no-op
        }

        glow.removeFromParent()
        glow.destroy({ children: true })
    }

    private clearFsCoinCollectEffects(): void {
        this.killFsCollectAnimations()
        this._pendingFsCollectMultipliers = []
        this._pendingFsCollectCount = 0
        this._fsCollectExpectedHits = 0
        this._fsCollectProcessedHits = 0
        this._isFsCollectFastForward = false
        this._fsCollectFinalLadderCount = -1
        this._fsCollectFinalMilestones = []
        Facade.isPopupBlockingInput = false
    }

    private trackFsCollectAnimation(animation: gsap.core.Animation): void {
        if (this._isFsCollectFastForward) {
            animation.timeScale(ReelsContainer.FS_COIN_COLLECT_SKIP_TIME_SCALE)
        }

        this._fsCoinCollectGlowTweens.push(animation)
    }

    private killFsCollectAnimations(): void {
        this._fsCoinCollectGlowTweens.forEach((animation) => animation.kill())
        this._fsCoinCollectGlowTweens = []

        this._fsCoinCollectGlowSpines.forEach((glow) => {
            this.destroyFsCoinCollectGlow(glow)
        })

        this._fsCoinCollectGlowSpines = []
    }

    private pruneFsCoinCollectAnimations(): void {
        this._fsCoinCollectGlowTweens = this._fsCoinCollectGlowTweens.filter((animation) => {
            try {
                const isPendingOrRunning = animation.totalProgress() < 1
                const isAttached = Boolean((animation as any).parent)
                return isPendingOrRunning && isAttached
            } catch {
                return false
            }
        })
    }

    private getVisibleMultiplierSymbols(): SymbolContainer[] {
        const extraTop = Number(Facade.reelsConfig.EXTRA_SYMBOLS_TOP || 0)
        const extraBottom = Number(Facade.reelsConfig.EXTRA_SYMBOLS_BOTTOM || 0)
        const symbols: SymbolContainer[] = []

        this._reels.forEach((reel) => {
            const toIndex = Math.max(extraTop, reel.symbols.length - extraBottom)
            for (let symbolIndex = extraTop; symbolIndex < toIndex; symbolIndex++) {
                const symbol = reel.symbols[symbolIndex]
                if (symbol?.index === SpecialSymbolType.MULTIPLIER) {
                    symbols.push(symbol)
                }
            }
        })

        return symbols
    }

    private applyFsCollectCoinFocus(): void {
        if (this._isFsCollectCoinFocusApplied) {
            return
        }

        const extraTop = Number(Facade.reelsConfig.EXTRA_SYMBOLS_TOP || 0)
        const visibleRows = Number(Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL || 0)

        this._reels.forEach((reel) => {
            reel.symbols.forEach((symbol: SymbolContainer, symbolIndex: number) => {
                const visibleRowIndex = symbolIndex - extraTop
                const isVisibleRow = visibleRowIndex >= 0 && visibleRowIndex < visibleRows

                if (!isVisibleRow) {
                    return
                }

                if (symbol.index === SpecialSymbolType.MULTIPLIER) {
                    symbol.shadowOut(false)
                } else {
                    symbol.shadowIn(false, ReelsContainer.FS_COLLECT_COIN_FOCUS_DIM_POWER)
                }
            })
        })

        this._isFsCollectCoinFocusApplied = true
    }

    private clearFsCollectCoinFocus(animate: boolean): void {
        if (!this._isFsCollectCoinFocusActive) {
            return
        }

        this._isFsCollectCoinFocusActive = false
        this._isFsCollectCoinFocusApplied = false

        const extraTop = Number(Facade.reelsConfig.EXTRA_SYMBOLS_TOP || 0)
        const visibleRows = Number(Facade.reelsConfig.VISIBLE_SYMBOLS_PER_REEL || 0)

        this._reels.forEach((reel) => {
            reel.symbols.forEach((symbol: SymbolContainer, symbolIndex: number) => {
                const visibleRowIndex = symbolIndex - extraTop
                const isVisibleRow = visibleRowIndex >= 0 && visibleRowIndex < visibleRows

                if (!isVisibleRow) {
                    return
                }

                symbol.shadowOut(animate)
            })
        })
    }

    private getVisibleReelSymbol(reelIndex: number, rowIndex: number): SymbolContainer | null {
        const reel = this._reels[reelIndex]
        if (!reel) {
            return null
        }

        const extraTop = Number(Facade.reelsConfig.EXTRA_SYMBOLS_TOP || 0)
        const symbol = reel.symbols[rowIndex + extraTop]

        return symbol || null
    }

    private getActiveLadderTarget(ladderContainer: PIXI.Container | null): PIXI.Container | null {
        if (!ladderContainer) {
            return null
        }

        const milestones = this.getLadderMilestonesFromModel()
        const activeIndex = this.getActiveLadderCoinIndex(milestones, ladderContainer)

        if (activeIndex < 0) {
            return null
        }

        return this.getLadderTargetByIndex(ladderContainer, activeIndex)
    }

    private findLadderContainerFromRoots(): PIXI.Container | null {
        const roots: Array<PIXI.Container | null | undefined> = [
            Facade.container as PIXI.Container | null,
            Facade.app?.stage as PIXI.Container | undefined,
            (Facade as any).stage as PIXI.Container | undefined,
        ]

        for (const root of roots) {
            const found = this.findContainerByName(root || null, ContainersNames.JACKPOT_LADDERS_CONTAINER)
            if (found) {
                return found
            }
        }

        return null
    }

    private getLadderMilestonesFromModel(): number[] {
        const runtimeMilestones = this.getRuntimeFsLadderMilestones()
        if (runtimeMilestones.length) {
            return runtimeMilestones
        }

        const source = (Facade.model?.spinData?.hold_and_win_extra as any)?.ladder_unlock_milestones_count
        if (Array.isArray(source)) {
            const resolved = source.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value))
            if (resolved.length) {
                ;(Facade as any).fsRuntimeLadderMilestones = resolved.slice()
                return resolved
            }
        }

        const runtimeSource = (Facade as any).fsRuntimeLadderMilestones
        if (!Array.isArray(runtimeSource)) {
            return []
        }

        return runtimeSource.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value))
    }

    private syncRuntimeFsLadderMilestonesFromModel(): void {
        if (!this.isFsCollectAllowed()) {
            return
        }

        if (this.getRuntimeFsLadderMilestones().length) {
            return
        }

        const source = (Facade.model?.spinData?.hold_and_win_extra as any)?.ladder_unlock_milestones_count
        if (!Array.isArray(source)) {
            return
        }

        const milestones = source.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value))
        if (!milestones.length) {
            return
        }

        ;(Facade as any).fsRuntimeLadderMilestones = milestones.slice()
        ;(Facade as any).fsRuntimeLadderCount = this._fsCollectFinalLadderCount
    }

    private captureFsCollectFinalLadderState(): void {
        const extra = Facade.model?.spinData?.hold_and_win_extra as any
        const milestones = extra?.ladder_unlock_milestones_count

        this._fsCollectFinalLadderCount = Number(extra?.ladder_count ?? -1)
        this._fsCollectFinalMilestones = Array.isArray(milestones)
            ? milestones.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value))
            : []
    }

    private applyFsCollectFinalLadderState(): boolean {
        if (this._fsCollectFinalLadderCount < 0 || !this._fsCollectFinalMilestones.length) {
            return false
        }

        ;(Facade as any).fsRuntimeLadderCount = this._fsCollectFinalLadderCount
        ;(Facade as any).fsRuntimeLadderMilestones = this._fsCollectFinalMilestones.slice()
        return true
    }

    private getRuntimeFsLadderMilestones(): number[] {
        const runtimeSource = (Facade as any).fsRuntimeLadderMilestones
        if (!Array.isArray(runtimeSource)) {
            return []
        }

        return runtimeSource.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value))
    }

    private assignFsCollectTargetLevels(coinCount: number): number[] {
        const projectedMilestones = this.getRuntimeFsLadderMilestones()

        return Array.from({ length: coinCount }, () => {
            const activeIndex = projectedMilestones.findIndex((value) => value > 0)
            if (activeIndex < 0) {
                return Math.max(0, projectedMilestones.length - 1)
            }

            projectedMilestones[activeIndex] = Math.max(0, projectedMilestones[activeIndex] - 1)
            return activeIndex
        })
    }

    private advanceRuntimeFsLadderMilestone(): void {
        const milestones = this.getRuntimeFsLadderMilestones()
        if (!milestones.length) {
            return
        }

        const activeIndex = milestones.findIndex((value: number) => Number.isFinite(value) && value > 0)
        if (activeIndex < 0) {
            return
        }

        milestones[activeIndex] = Math.max(0, milestones[activeIndex] - 1)
        ;(Facade as any).fsRuntimeLadderMilestones = milestones

        const runtimeCount = Number((Facade as any).fsRuntimeLadderCount ?? -1)
        if (runtimeCount >= 0) {
            ;(Facade as any).fsRuntimeLadderCount = runtimeCount + 1
        }
    }

    private isFsCollectAllowed(): boolean {
        const isFreeSpins = Boolean(Facade.model?.roundState?.isFreeSpins)
        const isHoldAndWinActive = Boolean(Facade.model?.spinState?.isHoldAndWinActive)
        const nextRoundType = String(Facade.model?.spinData?.next_round_type || '').toLowerCase()
        const nextIsRespin = nextRoundType === 'respin' || nextRoundType === 'respins'
        // 6+ coins during FS enter Hold & Win — do not fly coins to the jackpot ladder.
        const coinCount = this.getFsHoldAndWinTriggerCoinCount()
        const triggersHoldAndWin = coinCount >= 6
        // Every level was already unlocked before this spin — coins stay on the reels, worth 0.
        // Checked against the remaining capacity, not the response milestones: on the spin that
        // completes the ladder those are already all-zero, which used to cancel the whole collect.
        if (getLadderRemainingCoinCapacity() <= 0) {
            return false
        }

        return isFreeSpins && !isHoldAndWinActive && !nextIsRespin && !triggersHoldAndWin
    }

    private getFsHoldAndWinTriggerCoinCount(): number {
        const fromBonus = Number((Facade.model?.spinData?.bonus as any)?.num_coin_symbols)
        if (Number.isFinite(fromBonus) && fromBonus > 0) {
            return fromBonus
        }

        const fromExtra = Number((Facade.model?.spinData?.hold_and_win_extra as any)?.num_coin_symbols)
        if (Number.isFinite(fromExtra) && fromExtra > 0) {
            return fromExtra
        }

        return 0
    }

    private getLadderTargetByIndex(ladderContainer: PIXI.Container | null, levelIndex: number): PIXI.Container | null {
        if (!ladderContainer || levelIndex < 0) {
            return null
        }

        const numberTarget = this.findContainerByName(ladderContainer, `Jackpot Ladder Coin Value ${levelIndex + 1}`)
        if (numberTarget) {
            return numberTarget
        }

        const coinTarget = this.findContainerByName(ladderContainer, `Jackpot Ladder Coin ${levelIndex + 1}`)
        if (coinTarget) {
            return coinTarget
        }

        return this.findContainerByName(ladderContainer, 'Jackpot Ladder Spine Coin Steps')
    }

    private getActiveLadderCoinIndex(milestones: number[], ladderContainer: PIXI.Container): number {
        for (let index = 0; index < milestones.length; index++) {
            if (milestones[index] > 0) {
                return index
            }
        }

        // Fallback to currently highlighted ladder coin when milestones are not populated yet.
        for (let index = 0; index < 11; index++) {
            const numberTarget = this.findContainerByName(ladderContainer, `Jackpot Ladder Coin Value ${index + 1}`)
            if (numberTarget && numberTarget.alpha >= 0.9) {
                return index
            }

            const coinTarget = this.findContainerByName(ladderContainer, `Jackpot Ladder Coin ${index + 1}`)
            if (coinTarget && coinTarget.alpha >= 0.9) {
                return index
            }
        }

        return -1
    }

    private findContainerByName(container: PIXI.Container | null, name: string): PIXI.Container | null {
        if (!container) {
            return null
        }

        if ((container as any).name === name || (container as any).label === name) {
            return container
        }

        for (const child of container.children) {
            const match = this.findContainerByName(child as PIXI.Container, name)
            if (match) {
                return match
            }
        }

        return null
    }
}
